import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import pluralize from "pluralize";
import { is } from "ramda";
import {
  type CSSProperties,
  type ForwardedRef,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type AriaMenuProps,
  type AriaMenuTriggerProps,
  type Key,
  mergeProps,
  type Placement,
  useButton,
  useFocusRing,
} from "react-aria";
import { useTreeState } from "react-stately";

import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import { useDOMRef } from "../../hooks/useDomRef.ts";
import { type ContextualMenuOptions, useContextualMenu } from "../../hooks/useMenu.ts";
import { useResizeObserver } from "../../hooks/useResizeObserver.ts";
import formStyle from "../../styles/form.module.css";
import { forwardRef } from "../../utils.ts";
import { Badge } from "../Badge/Badge.tsx";
import { Description } from "../Description/Description.tsx";
import { Icon } from "../Icon/Icon.tsx";
import { SymbolIcon } from "../Icon/SymbolIcon.tsx";
import { Label } from "../Label/Label.tsx";
import { MenuListBox } from "../List/MenuListBox.tsx";
import { Popover } from "../Popover/Popover.tsx";
import { LoadingSpinner } from "../ProgressSpinner/LoadingSpinner.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import style from "./Select.module.css";

export type MultiSelection = "all" | Iterable<Key>;

export interface MultiSelectProps<T> extends AriaMenuProps<T>, AriaMenuTriggerProps {
  label?: string;
  isRequired?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  description?: ReactNode;
  placeholder?: string;
  multiOptionsText?: string;

  isLoading?: boolean;
  rendered?: (keys: MultiSelection) => ReactNode;

  style?: CSSProperties;
  icon?: ReactElement | MaterialSymbol;
  className?: string;
  width?: CSSProperties["minWidth"];
  maxWidth?: CSSProperties["maxWidth"];
  extraAction?: ReactElement;
  placement?: Placement;
}

const MultiSelectRender = <T extends object>(
  props: MultiSelectProps<T> & ContextualMenuOptions,
  ref: ForwardedRef<HTMLButtonElement>,
): ReactNode => {
  const {
    placeholder,
    multiOptionsText,
    isDisabled,
    icon,
    onSelectionChange,
    autoFocus,
    width,
    maxWidth,
    items,
    defaultSelectedKeys,
    selectionMode,
    rendered,
    isLoading,
    label,
    extraAction,
    isRequired,
    description,
    errorMessage,
    isInvalid,
    className,
    placement,
    selectedKeys,
  } = props;
  const triggerRef = useDOMRef(ref);

  const { state, triggerProps, menuProps } = useContextualMenu<HTMLButtonElement, T>(triggerRef, {
    ...props,
    isDisabled: !!isDisabled,
  });
  const { buttonProps } = useButton({ ...triggerProps, isDisabled: !!isDisabled }, triggerRef);
  const { isFocusVisible, isFocused, focusProps } = useFocusRing({ autoFocus: !!autoFocus });

  const stackRef = useRef<HTMLDivElement>(null);
  const stackRect = useResizeObserver(stackRef);
  const extraActionRef = useRef<HTMLDivElement>(null);
  const extraActionRect = useResizeObserver(extraActionRef);
  const listBoxWidth = useMemo(() => {
    let w = stackRect?.width ?? width;

    if (is(Number, w) && extraActionRect != null) {
      w = w - extraActionRect.width - 3; // 3 => column-gap
    }

    return w;
  }, [stackRect, extraActionRect, width]);

  const [selection, setSelection] = useState<MultiSelection>(selectedKeys ?? defaultSelectedKeys ?? new Set([]));

  const selectionSize = useMemo(
    () => (selection === "all" ? [...(items ?? [])].length : [...selection].length),
    [selection, items],
  );

  // controlled component, needed when changes on the selection are triggered from the outside
  useEffect(() => {
    setSelection(selectedKeys ?? new Set([]));
  }, [selectedKeys]);

  const treeStateListBoxProps = {
    ...mergeProps(props, menuProps),
    selectedKeys: selection,
    selectionMode: selectionMode ?? "multiple",
  };

  // Just use to render Item textValue or children
  const _state = useTreeState(treeStateListBoxProps);

  const dataAttributes = useDataAttributes(props);

  const renderedContent = useMemo(() => {
    if (selection !== "all" && selectionSize === 0) {
      return (
        <span className={style.placeholder}>{placeholder ?? `Select ${pluralize(multiOptionsText ?? "option")}`}</span>
      );
    }
    if (typeof rendered === "function") {
      return rendered(selection);
    }
    if (selection !== "all" && selectionSize === 1) {
      const item = _state.collection.getItem([...selection][0]);
      return item?.textValue ?? item?.rendered ?? (isLoading ? "Loading..." : "");
    }
    return `${selection === "all" ? "All" : selectionSize} ${multiOptionsText ?? "option"}${
      selection === "all" || selectionSize > 1 ? "s" : ""
    } selected`;
  }, [selection, selectionSize, rendered, multiOptionsText, placeholder, _state.collection, isLoading]);

  return (
    <>
      <div
        className={formStyle.input_layout}
        style={{ minWidth: width, maxWidth }}
        ref={stackRef}
        data-testid={`${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}multi-select`}
      >
        {label != undefined && (
          <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
            <Label>{label}</Label>
            <Spacer />
            {!isRequired && <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />}
          </HStack>
        )}
        <Description
          data-testid="multi-select-description"
          className={clsx(formStyle.input_layout_column1, {
            [formStyle.description_with_tag]: label != undefined && !isRequired,
          })}
        >
          {description}
        </Description>
        <button
          type="button"
          {...dataAttributes}
          {...mergeProps(focusProps, buttonProps)}
          className={clsx(style.select, formStyle.input_layout_column1, className, {
            [style.disabled]: isDisabled,
            [style.focus]: isFocusVisible || isFocused,
            [style.error]: isInvalid,
          })}
          style={{ ...style }}
          data-testid={`${
            dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""
          }multi-select__button`}
          ref={triggerRef}
        >
          {icon != undefined && <Icon icon={icon} className={style.icon} />}
          <div className={style.input}>{renderedContent}</div>
          <div className={style.action} aria-hidden="true">
            {!!isLoading && <LoadingSpinner size={16} style={{ marginRight: 3 }} />}
            {/* Width is 25px because button has `minWidth: 30` and icon is 20px */}
            <SymbolIcon icon={"unfold_more"} style={{ width: 25, flex: "0 0 25px" }} />
          </div>
        </button>
        {extraAction != undefined && (
          <HStack className={formStyle.input_layout_column2} ref={extraActionRef}>
            {extraAction}
          </HStack>
        )}
        {errorMessage != undefined && !!isInvalid && (
          <div
            role="alert"
            className={clsx(formStyle.error_message, formStyle.input_layout_column1)}
            data-testid="number-field-error"
          >
            {errorMessage}
          </div>
        )}
      </div>
      <Popover state={state} triggerRef={triggerRef} placement={placement ?? "bottom"} offset={5} containerPadding={10}>
        <MenuListBox
          {...treeStateListBoxProps}
          style={{ minWidth: listBoxWidth }}
          onSelectionChange={(keys) => {
            setSelection(keys);
            onSelectionChange?.(keys);
          }}
        />
      </Popover>
    </>
  );
};

export const MultiSelectMenu = forwardRef(MultiSelectRender);
